import { useState } from "react";
import { resourceService, taskService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  ASSIGNMENT_TEMPLATE_TYPES,
} from "@miplatform/constants";
import { useComponentState, useResourceGroup } from "@miplatform/hooks";
import { createUUID, sortOnField } from "@miplatform/utils";

const getAssignmentTemplateUUID = createUUID();
const getTemplateUUID = createUUID();
const updateTemplateUUID = createUUID();
const updateTemplateItemUUID = createUUID();
const createTemplateItemUUID = createUUID();
const deleteTemplateItemUUID = createUUID();
const getIssueTypesUUID = createUUID();

export function useAssignmentTemplateDetailsViewModel(id) {
  const [assignmentTemplate, setAssignmentTemplate] = useState({});
  const [issueTemplates, setIssueTemplates] = useState([]);
  const [assignmentTemplateOptions, setAssignmentTemplateOptions] = useState([]);
  const { resourceGroups, getResourceGroups } = useResourceGroup({
    service: resourceService,
  });

  const stateManager = useComponentState();

  const getAssignmentTemplateOptions = () => {
    stateManager.abort(getAssignmentTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getAll({
        sort: "name asc",
        filters: {
          type: ASSIGNMENT_TEMPLATE_TYPES.INSTALL,
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAssignmentTemplateOptions(
          response.data
            .filter(
              (item) =>
                item.type !== ASSIGNMENT_TEMPLATE_TYPES.REVIEW && item.id !== id
            )
            .map((item) => ({ label: item.name, value: item.id }))
        );
      }
    }, getAssignmentTemplateUUID);
  };

  const getAssignmentTemplateDetails = (id) => {
    stateManager.abort(getTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getById(id);
      if (response.status === HTTP_STATUSES.OK) {
        setAssignmentTemplate({
          ...response.data,
          qualificationIds: response.data?.qualifications
            ?.sort(sortOnField("name"))
            .map((item) => item.id),
        });
      }
    }, getTemplateUUID);
  };

  const createStepTemplateItem = (id, data) => {
    stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.createStepTemplateItem({
          templateId: id,
          data,
          toastMessageConfig: {
            success: {
              text: `${data.description} was created successfully.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.CREATED) {
        getAssignmentTemplateDetails(assignmentTemplate.id);
      }
    }, createTemplateItemUUID);
  };

  const deleteStepTemplateItem = ({
    assignmentTemplateId,
    stepTemplateId,
    data,
  }) => {
    stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.deleteStepTemplateItem({
          assignmentTemplateId,
          stepTemplateId,
          data,
          toastMessageConfig: {
            success: {
              text: `${assignmentTemplate.name} was deleted successfully.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.DELETED) {
        getAssignmentTemplateDetails(assignmentTemplate.id);
      }
    }, deleteTemplateItemUUID);
  };

  const updateAssignmentTemplate = (id, data) => {
    return stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.update({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `${assignmentTemplate.name} was updated successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        getAssignmentTemplateDetails(id);
        return response.data;
      }
      return null;
    }, updateTemplateUUID);
  };

  const updateStepTemplateItem = ({
    assignmentTemplateId,
    stepTemplateId,
    data,
  }) => {
    stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.updateStepTemplateItem({
          stepTemplateId,
          assignmentTemplateId,
          data,
          toastMessageConfig: {
            success: {
              text: `${data.description} Updated successfully.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.OK) {
        getAssignmentTemplateDetails(assignmentTemplate.id);
      }
    }, updateTemplateItemUUID);
  };

  const getIssueTemplates = () => {
    stateManager.abort(getIssueTypesUUID);
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        setIssueTemplates(
          response.data.map((item) => ({ label: item.name, value: item.id }))
        );
      }
    }, getIssueTypesUUID);
  };

  return {
    getAssignmentTemplateOptions,
    assignmentTemplateOptions,
    assignmentTemplate,
    loading: stateManager.isBusy(),
    updateAssignmentTemplate,
    getAssignmentTemplateDetails,
    createStepTemplateItem,
    updateStepTemplateItem,
    deleteStepTemplateItem,
    getIssueTemplates,
    issueTemplates,
    resourceGroups: resourceGroups.map((item) => ({
      label: item.name,
      value: item.id,
    })),
    getResourceGroups,
  };
}
